import { z } from "zod";
/**
 * The share cards' wire (`D257`, `D258`, `D260`).
 *
 * ── THE LINK IS A SERVER SWITCH, AND IT IS OFF ─────────────────────────────
 * A card carries the brand and nothing else until the app has a public store
 * listing (`D257`). Launching sets `SHARE_LINK_BASE_URL` on the API Worker;
 * `GET /share/config` answers it, and from that moment a card drawn on any
 * installed app carries `<base>?ref=<id>` — no bundle and no APK. Unset (the
 * default in both `wrangler.jsonc` blocks), the answer is `null` and nothing
 * anywhere draws a link.
 *
 * ── THE REF IS OPAQUE AND PER SHARE ────────────────────────────────────────
 * Twelve characters drawn on the device for each card that carries a link. It
 * names no account: the sharer is joined to it through `share_completed`,
 * which the API stamps with the sharer's token, and the newcomer through
 * `link_opened` and `signup_from_link`, which carry the same string back.
 * Nothing stores it server-side; the events are the whole record.
 */
/** `GET /share/config` — public: the site reads it before anyone signs in. */
export const SHARE_CONFIG_ROUTE = "/share/config";
/** `POST /share/link-opened` — public: the person who opens a link has no
 *  account yet, which is the point of the link. */
export const SHARE_LINK_OPENED_ROUTE = "/share/link-opened";
/** The query key a link carries its ref under. */
export const SHARE_REF_PARAM = "ref";
/**
 * An opaque share id. The device draws twelve characters from a 32-letter
 * alphabet; the server accepts 8 to 32 of `[0-9a-z]`, so the alphabet or the
 * length can change on the device without a server release.
 */
export const shareRef = z.string().regex(/^[0-9a-z]{8,32}$/);
export const shareConfigOutput = z.object({
    /**
     * The address a card links to, `?ref=` not yet appended — or `null` while
     * links are off (`D257`). Always `https`.
     */
    linkBaseUrl: z.url({ protocol: /^https$/ }).nullable(),
});
/** `POST /share/link-opened`'s body. Strict, like every input (`D172`). */
export const linkOpenedInput = z.strictObject({
    /** The bundle that opened the link — the `app_version` column. */
    appVersion: z.string().min(1).max(64).optional(),
    ref: shareRef,
});
/**
 * What a share card is about (`D260`). `workout` is the summary's 4:5 card
 * (`D201`); the rest are 9:16. A closed list: R2 SQL groups by it.
 */
export const SHARE_CARD_KINDS = [
    "workout",
    "record",
    "streak",
    "milestone",
    "calendar",
    "retro_week",
    "retro_month",
    "retro_year",
];
export const shareCardKind = z.enum(SHARE_CARD_KINDS);
/** What left the device: a still, the Retrospectiva's video, or words. */
export const SHARE_FORMATS = ["image", "video", "text"];
export const shareFormat = z.enum(SHARE_FORMATS);
/**
 * Where it went, as far as the platform says (`D258`): the receiving app's
 * Android package when the chooser reports one (`com.whatsapp`), `web_share`
 * for the browser's sheet (which never says), `download` for the desktop
 * fallback, `clipboard` for copied text, and `unknown` when the chooser
 * reported nothing. Never guessed.
 */
export const shareChannel = z.string().regex(/^[\w.:-]{1,100}$/);
/** `https://go.mypocketgym.app/` + `?ref=abc` — the one place a link is
 *  written, on the device and in the tests alike. */
export function shareLinkFor(base, ref) {
    const url = new URL(base);
    url.searchParams.set(SHARE_REF_PARAM, ref);
    return url.toString();
}
//# sourceMappingURL=share.js.map